import { useState, useEffect, useCallback } from 'react';

import apiService from '../services/api';
import { SkillObject } from '../types';

// LIFF 再起動をまたいで表示済みスキルを記憶するため localStorage を使用。
// sessionStorage は LIFF 終了でクリアされるため使えない。
const SEEN_KEY = 'gami-seen-skill-ids';

const getSeenIds = (): Set<string> => {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};

const addSeenIds = (ids: string[]): void => {
  try {
    const seen = getSeenIds();
    ids.forEach((id) => seen.add(id));
    localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(seen)));
  } catch {
    /* localStorage 不可環境では guard 無し（サーバ seen_at が最終的な冪等担保） */
  }
};

interface UseGetSkillCelebrationResult {
  /** 演出に渡す未読スキル（空なら非表示） */
  celebrationSkills: SkillObject[];
  /** OK 押下時に呼ぶ。演出を閉じる */
  closeCelebration: () => void;
}

/**
 * GET SKILL お祝い演出（未読スキル駆動）の取得・既読化フック（設計書 §5.3 / §6.3 p.21）。
 *
 * アプリ ロード時に本人の全店舗の未読スキル（seen_at IS NULL）を並列取得し、
 * あれば演出を表示する。複数店舗所属ワーカーのスキルも漏れなく検知する。
 * どの画面を開いても表示されるよう App 直下（GlobalGetSkillCelebration）にマウントする（修正依頼 #48）。
 *
 * 冪等性の2層構造:
 *   1. localStorage: 表示済みの skill_id を記録。LIFF 再起動後も残るため
 *      DB 更新が完了しなくても再表示を防げる（主防衛ライン）。
 *   2. DB (seen_at): markSkillsSeen を **表示前に await して** 呼び、
 *      seen_at を更新して次回 unseen から外す（副防衛ライン）。
 *      ※ fire-and-forget だと OK 押下でコンポーネントがアンマウントされるタイミングと
 *      競合し、mark-seen の HTTP リクエストが完了する前にアンマウントされて
 *      DB が更新されず、「画面を開くたびに GET SKILL が表示される」不具合があった。
 *
 * 認証はワーカー LINE フロント規約（line_access_token 自己所有経路。api.ts buildWorkerQuery）。
 * storeUserIds は呼び出し側が解決して渡す（storeUsers 全件の id 配列）。
 *
 * @param storeUserIds 対象 store_user の id 配列（空なら何もしない）
 * @param enabled      演出を有効化するか（既定 true。明示 false で抑止可能）
 */
export const useGetSkillCelebration = (
  storeUserIds: string[],
  enabled: boolean = true,
): UseGetSkillCelebrationResult => {
  const [celebrationSkills, setCelebrationSkills] = useState<SkillObject[]>([]);

  // storeUserIds の内容が変わった時だけ effect を再実行するため文字列化して比較する。
  const storeUserIdsKey = storeUserIds.join(',');

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!enabled || storeUserIds.length === 0) return;
      try {
        // 全店舗の unseen スキルを並列取得し、store_user_id と結果をペアで保持する。
        const results = await Promise.all(
          storeUserIds.map(async (suId) => {
            const res = await apiService.getUnseenSkills(suId);
            return {
              storeUserId: suId,
              skills: res.success && res.data ? res.data.unseen : [],
            };
          }),
        );
        if (cancelled) return;

        // 全店舗の unseen をフラット化し、localStorage で表示済みのものを除外する。
        const allUnseen = results.flatMap((r) => r.skills);
        const seenIds = getSeenIds();
        const freshSkills = allUnseen.filter((s) => !seenIds.has(s.skill_id));

        if (freshSkills.length > 0) {
          // 先に localStorage へ記録（LIFF が即時閉じられても再表示しない）。
          addSeenIds(freshSkills.map((s) => s.skill_id));

          // DB の seen_at を表示前に await して更新する（店舗ごとに PATCH）。
          const freshIds = new Set(freshSkills.map((s) => s.skill_id));
          await Promise.all(
            results
              .map((r) => {
                const ids = r.skills
                  .filter((s) => freshIds.has(s.skill_id))
                  .map((s) => s.skill_id);
                return ids.length > 0
                  ? apiService.markSkillsSeen(r.storeUserId, ids)
                  : null;
              })
              .filter(Boolean),
          );

          if (cancelled) return;
          setCelebrationSkills(freshSkills);
        }
      } catch {
        // 無認証 / ネットワーク等は非表示に倒す（演出は出さない）。
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeUserIdsKey, enabled]);

  const closeCelebration = useCallback(() => setCelebrationSkills([]), []);

  return { celebrationSkills, closeCelebration };
};

export default useGetSkillCelebration;
